import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  BookOpen, 
  ChevronRight,
  Flame
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const EncontrosPage = () => {
  const { celulas, loadingCelulas, loadEncontros, igrejaAtual } = useApp();
  
  const [allEncontros, setAllEncontros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllEncontros = async () => {
      if (!igrejaAtual || celulas.length === 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const encontrosPromises = celulas.map(celula => loadEncontros(celula.id));
      const results = await Promise.all(encontrosPromises);
      
      // Flatten and add celula info
      const flatEncontros = results.flatMap((encontros, index) => 
        (encontros || []).map(e => ({ ...e, celula: celulas[index] }))
      );
      
      // Sort by date descending
      flatEncontros.sort((a, b) => new Date(b.data) - new Date(a.data));
      
      setAllEncontros(flatEncontros);
      setLoading(false);
    };
    
    loadAllEncontros();
  }, [celulas, igrejaAtual]);

  const proximosEncontros = allEncontros.filter(e => 
    e.status === 'agendado' && new Date(e.data) >= new Date()
  );
  
  const encontrosRealizados = allEncontros.filter(e => e.status === 'realizado');

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-purple-500/10 via-background to-primary/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className="text-xs">
              {igrejaAtual?.nome || 'Igreja'}
            </Badge>
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
            Encontros
          </h1>
          <p className="text-muted-foreground mt-1">
            {loading ? 'Carregando...' : `${allEncontros.length} encontros no total`}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Próximos Encontros */}
          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
              Próximos Encontros
            </h2>
            
            {loading || loadingCelulas ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="card-elevated">
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : proximosEncontros.length === 0 ? (
              <Card className="card-elevated">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Nenhum encontro agendado</h3>
                  <p className="text-muted-foreground mb-4">
                    Acesse uma célula para agendar um novo encontro.
                  </p>
                  <Button asChild>
                    <Link to="/celulas">Ver Células</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {proximosEncontros.slice(0, 6).map((encontro, index) => (
                  <Link key={encontro.id} to={`/encontros/${encontro.id}`} data-testid={`encontro-card-${encontro.id}`}>
                    <Card 
                      className="card-elevated hover:border-primary/50 transition-all h-full animate-scale-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-purple-500/10">
                            <BookOpen className="w-5 h-5 text-purple-500" />
                          </div>
                          <Badge variant="default">Agendado</Badge>
                        </div>
                        
                        <h3 className="font-semibold text-foreground mb-1">
                          {encontro.celula?.nome || 'Célula'}
                        </h3>
                        
                        <p className="text-sm font-medium text-primary mb-2">
                          {format(new Date(encontro.data), "EEEE, d 'de' MMMM", { locale: ptBR })}
                        </p>
                        
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{encontro.horario}</span>
                          </div>
                          {encontro.local && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="truncate">{encontro.local}</span>
                            </div>
                          )}
                          {encontro.livro && (
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4" />
                              <span>{encontro.livro} {encontro.capitulo}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm text-muted-foreground">
                          <span>Ver detalhes</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Encontros Realizados */}
          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
              Encontros Realizados
            </h2>
            
            {loading || loadingCelulas ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="card-elevated">
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : encontrosRealizados.length === 0 ? (
              <Card className="card-elevated">
                <CardContent className="p-8 text-center">
                  <Flame className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhum encontro realizado ainda</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {encontrosRealizados.slice(0, 6).map((encontro, index) => (
                  <Link key={encontro.id} to={`/encontros/${encontro.id}`} data-testid={`encontro-realizado-${encontro.id}`}>
                    <Card 
                      className="card-elevated hover:border-primary/50 transition-all h-full animate-scale-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-muted">
                            <BookOpen className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <Badge variant="secondary">Realizado</Badge>
                        </div>
                        
                        <h3 className="font-semibold text-foreground mb-1">
                          {encontro.celula?.nome || 'Célula'}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          {format(new Date(encontro.data), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-600">
                            ✓ {encontro.presentes?.length || 0} presentes
                          </span>
                          <span className="text-red-500">
                            ✗ {encontro.ausentes?.length || 0} ausentes
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
